import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
  Fragment,
} from "react";
import { useAppSelector } from "stores";
import store from "stores/interfaces";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToFirstScrollableAncestor,
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { MouseSensor } from "modules/draggableSensor";
import {
  DropProps,
  isDropProps,
} from "stores/modules/firestoreModuleUtils/ordaring";
import styled from "styled-components";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { isEqual } from "lodash-es";

type SortableListProps = {
  ids: string[];
  noListMargin?: boolean;
  onReorder: (order: DropProps) => void;
  onSelect?: (id) => void;
  sortableItem: (id: string) => ReactNode;
};

const SortableList = ({
  ids,
  noListMargin,
  onReorder,
  onSelect,
  sortableItem,
}: SortableListProps) => {
  const roomId = useAppSelector((state) => store.getAppState(state, "roomId"));
  const [sortIds, setSortIds] = useState(ids);
  const prevIds = useRef(ids);

  useEffect(() => {
    if (!isEqual(prevIds.current, ids)) {
      prevIds.current = ids;
      setSortIds(ids);
    }
  }, [ids]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 2,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 2,
      },
    })
  );

  const debugScrollableAncestors = (node: Node | null) => {
    const result: any[] = [];

    let current =
      node instanceof HTMLElement ? node : node?.parentElement ?? null;

    while (current) {
      const style = window.getComputedStyle(current);

      result.push({
        tag: current.tagName,
        className: current.className,
        id: current.id,
        overflow: style.overflow,
        overflowY: style.overflowY,
        position: style.position,
        scrollHeight: current.scrollHeight,
        clientHeight: current.clientHeight,
        canActuallyScroll: current.scrollHeight > current.clientHeight,
      });

      if (style.position === "fixed") {
        break;
      }

      current = current.parentElement;
    }
  };

  const onDragStart = useCallback(
    (e: DragStartEvent) => {
      if (onSelect) {
        onSelect(e.active.id.toString());
      }
    },
    [onSelect]
  );
  const onDragEnd = useCallback(
    async (e: DragEndEvent) => {
      debugScrollableAncestors(e.activatorEvent?.target as Node | null);

      const { active, over } = e;
      const order = {
        destination: over?.data.current?.sortable.index,
        source: active.data.current?.sortable.index,
      };

      if (roomId && isDropProps(order) && order.destination !== order.source) {
        setSortIds((c) => {
          const nextIds = arrayMove(c, order.source, order.destination);
          prevIds.current = nextIds;
          return nextIds;
        });
        await onReorder(order);
      }
    },
    [roomId, onReorder]
  );
  return (
    <SortableContent noListMargin={noListMargin}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[
          restrictToVerticalAxis,
          restrictToParentElement,
          restrictToFirstScrollableAncestor,
        ]}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        autoScroll={{
          canScroll: (el) => {
            return (
              el instanceof HTMLElement &&
              el.classList.contains("scrollable-list")
            );
          },
        }}
      >
        <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
          {sortIds.map((id) => (
            <Fragment key={id}>{sortableItem(id)}</Fragment>
          ))}
        </SortableContext>
      </DndContext>
    </SortableContent>
  );
};

const SortableContent = styled.div<{ noListMargin?: boolean }>`
  margin-top: ${(props) => (props.noListMargin ? "0" : "8px")};
  margin-bottom: ${(props) => (props.noListMargin ? "0" : "8px")};
  isolation: isolate;
`;

export default memo(SortableList);
